import React, { useState, useEffect, useCallback } from 'react';
import { FiClock } from 'react-icons/fi';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaForward,
  FaSyncAlt,
  FaTrophy,
  FaInfoCircle,
  FaAngleDown,
  FaAngleUp,
  FaList,
  FaBolt,
  FaUnlock,
  FaAngleLeft,
  FaAngleRight,
} from 'react-icons/fa';
import apiService from '../services/apiService';

interface SchedulerJob {
  jobId: string;
  jobName: string;
  status: string;
  message?: string;
  startedAt: string;
  pickedUpAt?: string;
  completedAt?: string;
  duration?: string;
  details?: any;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type TabType = 'all' | 'active';

const SchedulerJobs: React.FC = () => {
  const [jobs, setJobs] = useState<SchedulerJob[]>([]);
  const [activeJobs, setActiveJobs] = useState<SchedulerJob[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const loadJobs = useCallback(async (page: number = 1) => {
    setLoading(true);
    setError('');

    let activeData: any[] = [];

    try {
      const result = await apiService.getSchedulerJobs(page);
      const jobsList = result?.jobs || [];
      setJobs(Array.isArray(jobsList) ? jobsList : []);
      setPagination({
        total: result?.total || 0,
        page: result?.page || 1,
        limit: result?.limit || 20,
        totalPages: result?.totalPages || 0,
      });
    } catch (err: any) {
      console.error('Error loading scheduler jobs:', err);
      setError(err?.message || 'Failed to load scheduler jobs');
    }

    try {
      activeData = await apiService.getActiveJobs();
    } catch (err: any) {
      console.error('Error loading active jobs:', err);
    }

    setActiveJobs(Array.isArray(activeData) ? activeData : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setExpandedJobId(null);
    loadJobs(newPage);
  };

  const toggleJobExpand = (job: SchedulerJob) => {
    setExpandedJobId(expandedJobId === job.jobId ? null : job.jobId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getJobIcon = (jobName: string) => {
    if (jobName.includes('sync-scorecard')) return FaSyncAlt;
    if (jobName.includes('fantasy-points')) return FaTrophy;
    if (jobName.includes('unlock')) return FaUnlock;
    return FiClock;
  };

  const getJobColor = (jobName: string) => {
    if (jobName.includes('sync-scorecard')) return { bg: 'bg-blue-50 dark:bg-primary-500/10', border: 'border-blue-100 dark:border-primary-500/20', icon: 'text-blue-600 dark:text-primary-400' };
    if (jobName.includes('fantasy-points')) return { bg: 'bg-amber-50 dark:bg-warning-500/10', border: 'border-amber-100 dark:border-warning-500/20', icon: 'text-amber-600 dark:text-warning-400' };
    if (jobName.includes('unlock')) return { bg: 'bg-purple-50 dark:bg-secondary-500/10', border: 'border-purple-100 dark:border-secondary-500/20', icon: 'text-purple-600 dark:text-secondary-400' };
    return { bg: 'bg-indigo-50 dark:bg-indigo-500/10', border: 'border-indigo-100 dark:border-indigo-500/20', icon: 'text-indigo-600 dark:text-indigo-400' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'bg-emerald-100 dark:bg-success-500/20 text-emerald-700 dark:text-success-400 border-emerald-200 dark:border-success-500/30';
      case 'failed':
      case 'error':
        return 'bg-red-100 dark:bg-danger-500/20 text-red-700 dark:text-danger-400 border-red-200 dark:border-danger-500/30';
      case 'running':
      case 'active':
        return 'bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30';
      default:
        return 'bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-500/30';
    }
  };

  // Extract the kanban data from the nested details structure
  const getKanbanData = (job: SchedulerJob) => {
    const d = job.details;
    if (!d) return null;

    // For update-fantasy-points and sync-scorecards: details.details contains succeeded/failed/skipped
    const inner = d.details || {};
    const succeeded = inner.succeeded || d.succeeded || [];
    const failed = inner.failed || d.failed || d.errors || [];
    const skipped = inner.skipped || d.skipped || [];
    const series = d.series || [];

    // For process-unlocking-only
    const unlockedContests = d.unlockedContests || [];
    const errors = d.errors || [];

    return { succeeded, failed, skipped, series, unlockedContests, errors };
  };

  const tabs = [
    { id: 'all' as TabType, label: 'All Jobs', icon: FaList, count: pagination.total },
    { id: 'active' as TabType, label: 'Active Jobs', icon: FaBolt, count: activeJobs.length },
  ];

  // Render Kanban board for a job's details
  const renderKanbanBoard = (job: SchedulerJob) => {
    const kanban = getKanbanData(job);
    if (!kanban) return null;

    const isUnlocking = job.jobName.includes('unlock');

    // For unlocking jobs, show a simpler view
    if (isUnlocking) {
      return (
        <div className="mt-4 space-y-3">
          {job.details?.message && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{job.details.message}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border-2 border-emerald-300 dark:border-emerald-600 overflow-hidden">
              <div className="bg-emerald-50 dark:bg-emerald-950/40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaUnlock className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
                  <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Unlocked</span>
                </div>
                <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {kanban.unlockedContests.length}
                </span>
              </div>
              <div className="p-3 bg-white/50 dark:bg-dark-500/30">
                {kanban.unlockedContests.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">No contests unlocked</div>
                ) : (
                  <div className="space-y-2">
                    {kanban.unlockedContests.map((c: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm">
                        <pre className="text-[10px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">{JSON.stringify(c, null, 2)}</pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-xl border-2 border-red-300 dark:border-red-600 overflow-hidden">
              <div className="bg-red-50 dark:bg-red-950/40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaTimesCircle className="h-4 w-4 text-red-700 dark:text-red-400" />
                  <span className="text-sm font-bold text-red-700 dark:text-red-400">Errors</span>
                </div>
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {kanban.errors.length}
                </span>
              </div>
              <div className="p-3 bg-white/50 dark:bg-dark-500/30">
                {kanban.errors.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm italic">No errors</div>
                ) : (
                  <div className="space-y-2">
                    {kanban.errors.map((e: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-dark-400/60 rounded-lg border border-red-200 dark:border-red-500/20 p-3 shadow-sm text-xs text-red-700 dark:text-red-400">
                        {typeof e === 'string' ? e : JSON.stringify(e)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // For sync-scorecards / update-fantasy-points
    const columns = [
      {
        title: 'Succeeded',
        items: kanban.succeeded,
        icon: FaCheckCircle,
        borderColor: 'border-emerald-300 dark:border-emerald-600',
        headerBg: 'bg-emerald-50 dark:bg-emerald-950/40',
        headerText: 'text-emerald-700 dark:text-emerald-400',
        badgeBg: 'bg-emerald-500',
      },
      {
        title: 'Failed',
        items: kanban.failed,
        icon: FaTimesCircle,
        borderColor: 'border-red-300 dark:border-red-600',
        headerBg: 'bg-red-50 dark:bg-red-950/40',
        headerText: 'text-red-700 dark:text-red-400',
        badgeBg: 'bg-red-500',
      },
      {
        title: 'Skipped',
        items: kanban.skipped,
        icon: FaForward,
        borderColor: 'border-amber-300 dark:border-amber-600',
        headerBg: 'bg-amber-50 dark:bg-amber-950/40',
        headerText: 'text-amber-700 dark:text-amber-400',
        badgeBg: 'bg-amber-500',
      },
    ];

    return (
      <div className="mt-4">
        {/* Summary message */}
        {job.details?.message && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{job.details.message}</p>
        )}

        {/* Series tags */}
        {kanban.series.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {kanban.series.map((s: any) => (
              <span
                key={s.seriesId}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border border-blue-200 dark:border-primary-500/30"
              >
                {s.seriesName}
              </span>
            ))}
          </div>
        )}

        {/* Summary stats row */}
        {job.details && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {job.details.seriesProcessed != null && (
              <div className="text-center p-2 bg-gray-50 dark:bg-dark-300/30 rounded-lg">
                <p className="text-lg font-bold text-gray-800 dark:text-gray-200">{job.details.seriesProcessed}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Series</p>
              </div>
            )}
            {job.details.matchesRefreshed != null && (
              <div className="text-center p-2 bg-blue-50 dark:bg-primary-500/10 rounded-lg">
                <p className="text-lg font-bold text-blue-700 dark:text-primary-400">{job.details.matchesRefreshed}</p>
                <p className="text-[10px] text-blue-600 dark:text-primary-500">Refreshed</p>
              </div>
            )}
            {job.details.matchesProcessed != null && (
              <div className="text-center p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{job.details.matchesProcessed}</p>
                <p className="text-[10px] text-emerald-600 dark:text-emerald-500">Processed</p>
              </div>
            )}
            {job.details.contestsProcessed != null && (
              <div className="text-center p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg">
                <p className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{job.details.contestsProcessed}</p>
                <p className="text-[10px] text-emerald-600 dark:text-emerald-500">Contests</p>
              </div>
            )}
            {job.details.contestsSkipped != null && (
              <div className="text-center p-2 bg-amber-50 dark:bg-amber-500/10 rounded-lg">
                <p className="text-lg font-bold text-amber-700 dark:text-amber-400">{job.details.contestsSkipped}</p>
                <p className="text-[10px] text-amber-600 dark:text-amber-500">Skipped</p>
              </div>
            )}
            {job.details.matchesFailed != null && job.details.matchesFailed > 0 && (
              <div className="text-center p-2 bg-red-50 dark:bg-red-500/10 rounded-lg">
                <p className="text-lg font-bold text-red-700 dark:text-red-400">{job.details.matchesFailed}</p>
                <p className="text-[10px] text-red-600 dark:text-red-500">Failed</p>
              </div>
            )}
          </div>
        )}

        {/* Kanban Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {columns.map((col) => {
            const Icon = col.icon;
            return (
              <div key={col.title} className={`rounded-xl border-2 ${col.borderColor} overflow-hidden`}>
                <div className={`${col.headerBg} px-4 py-3 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${col.headerText}`} />
                    <span className={`text-sm font-bold ${col.headerText}`}>{col.title}</span>
                  </div>
                  <span className={`${col.badgeBg} text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center`}>
                    {col.items.length}
                  </span>
                </div>
                <div className="p-3 space-y-3 max-h-96 overflow-y-auto bg-white/50 dark:bg-dark-500/30">
                  {col.items.length === 0 ? (
                    <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm italic">No items</div>
                  ) : (
                    col.items.map((item: any, idx: number) => (
                      <KanbanCard key={idx} item={item} jobName={job.jobName} columnType={col.title} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render a single job row
  const renderJobRow = (job: SchedulerJob) => {
    const Icon = getJobIcon(job.jobName);
    const colors = getJobColor(job.jobName);
    const isExpanded = expandedJobId === job.jobId;
    const kanban = getKanbanData(job);

    return (
      <div
        key={job.jobId}
        className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden transition-all"
      >
        <button
          onClick={() => toggleJobExpand(job)}
          className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-400/40 transition-colors"
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`p-2 ${colors.bg} rounded-lg border ${colors.border} flex-shrink-0`}>
              <Icon className={`h-5 w-5 ${colors.icon}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">{job.jobName}</h3>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(job.status)}`}>
                  {job.status}
                </span>
                {job.duration && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">{job.duration}</span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span>{formatDate(job.startedAt)}</span>
                {job.message && (
                  <span className="truncate max-w-xs">{job.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* Summary Badges */}
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            {kanban && kanban.succeeded.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                <FaCheckCircle className="h-3 w-3" /> {kanban.succeeded.length}
              </span>
            )}
            {kanban && kanban.failed.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                <FaTimesCircle className="h-3 w-3" /> {kanban.failed.length}
              </span>
            )}
            {kanban && kanban.skipped.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">
                <FaForward className="h-3 w-3" /> {kanban.skipped.length}
              </span>
            )}
            {isExpanded ? <FaAngleUp className="h-5 w-5 text-gray-400" /> : <FaAngleDown className="h-5 w-5 text-gray-400" />}
          </div>
        </button>

        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-100 dark:border-dark-300/30">
            {renderKanbanBoard(job)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <FaSyncAlt className="h-8 w-8 text-blue-500 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading scheduler jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-indigo-100 dark:bg-indigo-500/20 rounded-lg border border-indigo-200 dark:border-indigo-500/30">
            <FiClock className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Scheduler Jobs</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {pagination.total} total job(s) &middot; {activeJobs.length} active
            </p>
          </div>
        </div>
        <button
          onClick={() => loadJobs(pagination.page)}
          disabled={loading}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50"
        >
          <FaSyncAlt className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-danger-500/20 border border-red-200 dark:border-danger-500/30 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <FaTimesCircle className="h-5 w-5 text-red-600 dark:text-danger-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 dark:text-danger-500">{error}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm dark:shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-dark-300/40">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setExpandedJobId(null); }}
                className={`flex items-center gap-2 px-6 py-3.5 text-sm font-semibold transition-all relative ${
                  isActive
                    ? 'text-indigo-700 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                <span>{tab.label}</span>
                <span className={`ml-1 px-2 py-0.5 text-xs font-bold rounded-full ${
                  isActive
                    ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400'
                    : 'bg-gray-100 dark:bg-dark-300/50 text-gray-500 dark:text-gray-400'
                }`}>
                  {tab.count}
                </span>
                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400" />}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {/* All Jobs Tab */}
          {activeTab === 'all' && (
            <div className="space-y-4">
              {jobs.length === 0 && !error ? (
                <div className="text-center py-12">
                  <FiClock className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Jobs Found</h3>
                  <p className="text-gray-600 dark:text-gray-400">No scheduler job runs have been recorded yet.</p>
                </div>
              ) : (
                <>
                  {jobs.map((job) => renderJobRow(job))}

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-dark-300/40">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Page {pagination.page} of {pagination.totalPages} ({pagination.total} jobs)
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <FaAngleLeft className="h-4 w-4 mr-1" /> Prev
                        </button>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-400 border border-gray-300 dark:border-dark-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Next <FaAngleRight className="h-4 w-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Active Jobs Tab */}
          {activeTab === 'active' && (
            <div className="space-y-4">
              {activeJobs.length === 0 ? (
                <div className="text-center py-12">
                  <FaBolt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Active Jobs</h3>
                  <p className="text-gray-600 dark:text-gray-400">There are no jobs currently running.</p>
                </div>
              ) : (
                activeJobs.map((job) => {
                  const Icon = getJobIcon(job.jobName);
                  return (
                    <div key={job.jobId} className="bg-white dark:bg-dark-400/60 rounded-xl border-2 border-blue-300 dark:border-primary-500/40 shadow-sm overflow-hidden">
                      <div className="px-6 py-4 flex items-center gap-4">
                        <div className="relative flex-shrink-0">
                          <div className="p-2 bg-blue-50 dark:bg-primary-500/10 rounded-lg border border-blue-200 dark:border-primary-500/20">
                            <Icon className="h-5 w-5 text-blue-600 dark:text-primary-400" />
                          </div>
                          <div className="absolute -top-1 -right-1 h-3 w-3">
                            <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75" />
                            <div className="absolute inset-0 bg-blue-500 rounded-full" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-base font-bold text-gray-900 dark:text-white">{job.jobName}</h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-blue-100 dark:bg-primary-500/20 text-blue-700 dark:text-primary-400 border-blue-200 dark:border-primary-500/30">
                              Running
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>Started: {formatDate(job.startedAt)}</span>
                            {job.message && <span>{job.message}</span>}
                          </div>
                        </div>
                        {job.details?.series && job.details.series.length > 0 && (
                          <div className="flex flex-wrap gap-1 flex-shrink-0">
                            {job.details.series.map((s: any) => (
                              <span key={s.seriesId} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 dark:bg-primary-500/10 text-blue-600 dark:text-primary-400 border border-blue-100 dark:border-primary-500/20">
                                {s.seriesName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Individual Kanban Card Component
const KanbanCard: React.FC<{ item: any; jobName: string; columnType: string }> = ({ item, jobName, columnType }) => {
  const isSyncScorecard = jobName.includes('sync-scorecard');
  const isFantasyPoints = jobName.includes('fantasy-points');

  // Sync Scorecard succeeded card
  if (isSyncScorecard && columnType === 'Succeeded') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono font-bold text-gray-700 dark:text-gray-300">#{item.matchId}</span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white font-semibold">
            {item.matchDesc || `Match ${item.matchOrderId}`}
          </span>
        </div>
        {(item.localTeam || item.visitorTeam) && (
          <div className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
            <span className="bg-gray-100 dark:bg-dark-300/50 px-2 py-0.5 rounded text-xs">{item.localTeam || '?'}</span>
            <span className="text-xs text-gray-400">vs</span>
            <span className="bg-gray-100 dark:bg-dark-300/50 px-2 py-0.5 rounded text-xs">{item.visitorTeam || '?'}</span>
          </div>
        )}
        <div className="text-[10px] text-gray-500 dark:text-gray-400">{item.seriesName}</div>
      </div>
    );
  }

  // Fantasy Points succeeded card
  if (isFantasyPoints && columnType === 'Succeeded') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="mb-1">
          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{item.contestName}</span>
        </div>
        <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">{item.seriesName}</div>

        {item.newMatches && item.newMatches.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">
              New Matches ({item.newMatchCount})
            </p>
            <div className="space-y-1">
              {item.newMatches.map((m: any) => (
                <div key={m.matchId} className="flex items-center gap-2 text-[11px] text-gray-700 dark:text-gray-300 bg-emerald-50 dark:bg-emerald-500/10 rounded px-2 py-1">
                  <span className="font-mono text-[10px]">#{m.matchId}</span>
                  <span>{m.matchDesc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {item.result && (
          <div className="grid grid-cols-3 gap-1 mt-2">
            <div className="text-center p-1 bg-gray-50 dark:bg-dark-300/30 rounded">
              <p className="text-xs font-bold text-gray-800 dark:text-gray-200">{item.result.totalMatches}</p>
              <p className="text-[9px] text-gray-500">Total</p>
            </div>
            <div className="text-center p-1 bg-emerald-50 dark:bg-emerald-500/10 rounded">
              <p className="text-xs font-bold text-emerald-700 dark:text-emerald-400">{item.result.processed}</p>
              <p className="text-[9px] text-emerald-600 dark:text-emerald-500">Processed</p>
            </div>
            <div className="text-center p-1 bg-red-50 dark:bg-red-500/10 rounded">
              <p className="text-xs font-bold text-red-700 dark:text-red-400">{item.result.failed}</p>
              <p className="text-[9px] text-red-600 dark:text-red-500">Failed</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fantasy Points / sync-scorecard skipped card
  if (columnType === 'Skipped') {
    return (
      <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
        <div className="mb-1">
          <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{item.contestName || item.matchDesc || 'Item'}</span>
        </div>
        {item.seriesName && (
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">{item.seriesName}</div>
        )}
        {item.alreadyProcessed != null && (
          <div className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded px-2 py-1">
            <FaInfoCircle className="h-3 w-3 flex-shrink-0" />
            <span>Already processed ({item.alreadyProcessed}/{item.completedMatches} matches)</span>
          </div>
        )}
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="bg-white dark:bg-dark-400/60 rounded-lg border border-gray-200 dark:border-dark-300/40 p-3 shadow-sm hover:shadow-md transition-shadow">
      <pre className="text-[10px] text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words overflow-hidden max-h-32">
        {JSON.stringify(item, null, 2)}
      </pre>
    </div>
  );
};

export default SchedulerJobs;
